import { FC, useCallback, useState } from "react";
import { Alert, Button, Icon, Input } from "@components";
import { ColorsEnum } from "@utilities/enums";
import { ErrorType } from "@utilities/types";

interface Props {
    title: string
    subTitle: string
    isLoading: boolean
    isEmailSent: boolean
    error: ErrorType | null
    onSentEmail: (email: string) => void
    onResendEmail: () => void
    onGoToLoginPress: () => void
    onClearStore?: () => void
}

export const EmailVerification: FC<Props> = ({ 
    title,
    subTitle,
    isLoading,
    isEmailSent,
    error,
    onSentEmail,
    onResendEmail,
    onGoToLoginPress,
    onClearStore
}) => {

    const [email, setEmail] = useState("");

    const handleSentEmail = useCallback(()=> {
        if (email) {
            onSentEmail(email);
        };
    }, [email, onSentEmail]);

    return (
        <div className="p-1 items-center max-w-md mx-auto">
            <h1 className="text-2xl font-semibold text-center text-secondary mb-10">
                {title}
            </h1>

            {
                error && (
                    <Alert
                        showCloseButton
                        type="error"
                        message={error.message}
                    />
                )
            }

            <div className="space-y-6">
                {
                    !isEmailSent ? (
                        <>
                            <p className="text-center text-gray-600">
                                {subTitle}
                            </p>
                            <Input
                                type="email"
                                placeholder="Votre adresse email"
                                className="input input-bordered rounded-full"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                leftIcon={
                                    <Icon
                                        iconName="AtSign"
                                        size={25}
                                        color={ColorsEnum.PRIMARY}
                                    />
                                }
                            />
                            <Button
                                onClick={handleSentEmail}
                                type="button"
                                className="w-full"
                                disabled={!email || isLoading}
                            >
                                {!isLoading ? "Envoyer le lien de vérification" : "Chargement..."}
                            </Button>
                        </>
                    ) : (
                        <div className="text-center space-y-4">
                            <div className="bg-base-200 p-4 rounded-lg">
                                <p className="text-gray-600">
                                    Un lien de vérification a été envoyé à <span className="font-semibold">{email}</span>
                                </p>
                                <p className="text-sm text-gray-500 mt-2">
                                    Veuillez vérifier votre boîte de réception et vos spams
                                </p>
                            </div>

                            <Button
                                onClick={onResendEmail}
                                type="button"
                                variant="secondary"
                                className="w-full"
                                disabled={isLoading}
                            >
                                {!isLoading ? "Je n'ai pas reçu le mail" : "Chargement..."}
                            </Button>

                            {onClearStore && (
                                <Button
                                    onClick={onClearStore}
                                    type="button"
                                    variant="warning"
                                    className="w-full"
                                >
                                    Nettoyer et recommencer
                                </Button>
                            )}
                        </div>
                    )
                }

                <div className="text-center">
                    <Button
                        onClick={onGoToLoginPress}
                        className="text-primary btn-link"
                    >
                        J'ai déjà un compte
                    </Button>
                </div>
            </div>
        </div>
    );
};
